import { DialogFormFieldContainer } from '@/common/dialog/DialogFormFieldContainer';
import { Form } from '@/common/form/Form';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';

export const AbvPbmRateAddDialog = ({ closeDialog, lbeId }) => {

  const rsrc = useRsrc();
  const formRef = useRef();
  const module = useModuleFromRoute();

  const [fieldDefs, setFieldDefs] = useState();

  const [formValues, setFormValues] = useState({});

  const [prodId, setProdId] = useState();
  const [channelId, setChannelId] = useState();

  const [totalLbeBaseAmt, setTotalLbeBaseAmt] = useState();
  const [totalInput, setTotalInput] = useState(0);

  const [rows, setRows] = useState([{ pbm: null, value: null, rate: null }]);
  const [pbms, setPbms] = useState();

  const entity = 'PBM_RATE';
  const actionPluginCode = 'PBM_RATE_ACTION_PLUGIN';

  useEffect(() => {

    rsrc.pluginFieldDefs(module, actionPluginCode, entity).then(fieldDefs => {

      const fieldToFieldDef = Object.fromEntries(fieldDefs.map(fieldDef => [fieldDef.field, fieldDef]));
      fieldToFieldDef.prodId.expr = ExprUtils.whereParams('hierCode', 'PROD_HIER', 'isLeaf', true);
      fieldToFieldDef.channelId.expr = ExprUtils.whereParams('code', ['MCO', 'MPD'], 'hierCode', 'RESERVE_CHANNEL_HIER');

      fieldDefs = Object.values(fieldToFieldDef);

      fieldDefs.sort((a, b) => (a.precedence || 0) - (b.precedence || 0));
      setFieldDefs(fieldDefs);

    });

    let rowsReq = {
      expr: ExprUtils.whereParams('hierCode', 'PBM_HIER', 'isLeaf', true)
    };

    rsrc.rows(module, 'HIER', rowsReq).then(pbms => {
      pbms.sort((a, b) => { return a.name < b.name ? -1 : 1 });
      setPbms(pbms);
    });

  }, []);

  useEffect(() => {
    setProdId(formValues.prodId ?? null);
    setChannelId(formValues.channelId ?? null);
  }, [formValues.prodId, formValues.channelId]);

  useEffect(() => {
    if (prodId == null || channelId == null) {
      // Cannot determine the total base amount from the LBE if all fields are not filled out
      setTotalLbeBaseAmt(null);
      return;
    }

    // Update the Total for the calculation of the Rates
    let reqParam = {
      entity,
      prodId,
      channelId,
      lbeId
    };

    rsrc.data(module, 'PBM_RATE_TOTAL_DATA_PLUGIN', reqParam, entity).then(total => {
      setTotalLbeBaseAmt(total);
    });

  }, [prodId, channelId]);

  useEffect(() => {
    let newInput = 0;
    rows.forEach(row => { newInput += row.value != null ? Number(row.value) : 0; });
    setTotalInput(newInput);

  }, [rows]);

  useEffect(() => {
    updateRates();
  }, [totalLbeBaseAmt]);

  const submit = () => {
    if (formRef.current.hasErrorState()) {
      formRef.current.touchAll();
      return;
    }

    const actionReq = {
      ...formRef.current.formValues,
      lbeId,
      rows
    };

    const job = {
      code: actionPluginCode.replace('_ACTION_PLUGIN', ''),
      module,
      entity,
      rsrcCode: actionPluginCode,
      reqParamJson: JSON.stringify(actionReq),
      cat: 'ACTION'
    }

    rsrc.save('skdr', 'JOB', job).then(job => {
      rsrc.start(job.id);
      closeDialog();
    });
  }

  const handleRowPbmChange = (index, pbmSelected) => {
    setRows(rows => {
      const newRows = [...rows];
      newRows[index] = { ...newRows[index], pbm: pbmSelected };

      // Check that the user entered a PBM in the last row. If so, add a new row
      const isLastRow = index === newRows.length - 1;
      if (isLastRow && pbmSelected) {
        newRows.push({ pbm: null, value: null, rate: null });
      }

      return newRows;
    });
  }

  const handleRowValueChange = (index, newValue) => {
    setRows(rows => {
      const newRows = [...rows];
      newRows[index] = { ...newRows[index], value: newValue };
      return newRows;
    });

    updateRates();
  }

  const updateRates = () => {
    setRows(rows =>
      rows.map(row => ({
        ...row,
        rate:
          row.value != null && totalLbeBaseAmt != null
            ? Number(row.value) / totalLbeBaseAmt
            : null
      }))
    );
  }

  const handlePasteValues = (event, startIndex) => {
    const text = event.clipboardData.getData('text');
    if (!text) return;

    event.preventDefault();

    const lines = text.replace(/\r\n/g, '\n').split('\n');

    setRows(rows => {
      const newRows = [...rows];

      lines.forEach((line, offset) => {

        let index = startIndex + offset;
        const pastedValue = line.trim();

        if (!newRows[index]) {
          // Create a new row if there isn't one that already exists
          newRows[index] = { type: null, value: null, rate: null };
        }

        newRows[index] = { ...newRows[index], value: pastedValue };
      });

      return newRows;

    });

    updateRates();
  }

  return (
    <Dialog open fullWidth onClose={() => closeDialog()}>
      <DialogTitle>PBM Rate</DialogTitle>
      <DialogContent>
        {fieldDefs && pbms && <>
          {/* Action Plugin Form Fields */}
          <Form
            ref={formRef}
            fieldDefs={fieldDefs}
            container={DialogFormFieldContainer}
            formValues={formValues}
            setFormValues={setFormValues}
          />
          <Box mt={2} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Total */}
            <NumericFormat
              customInput={TextField}
              label="Total LBE Base Amount"
              sx={{ flex: '0 0 25%' }}
              thousandSeparator
              value={totalLbeBaseAmt}
              disabled
            />
            {/* Total Input */}
            <NumericFormat
              customInput={TextField}
              label="Total Input"
              sx={{ flex: '0 0 25%' }}
              thousandSeparator
              value={totalInput}
              disabled
            />
            {/* Total Input Percentage */}
            <NumericFormat
              customInput={TextField}
              label="Total Rate"
              value={(totalLbeBaseAmt ? totalInput / totalLbeBaseAmt : 0) * 100}
              disabled
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator
              suffix="%"
              sx={{ flex: '0 0 15%' }}
            />
          </Box>
          {/* PBM selection rows */}
          <Box mt={2}>
            {rows.map((row, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: index === 0 ? 0 : 1 }}
              >
                {/* PBM Selection*/}
                <TextField
                  select
                  label="PBM"
                  value={row.pbm}
                  onChange={(event) => handleRowPbmChange(index, event.target.value)}
                  sx={{ flex: '0 0 50%' }}
                >
                  {pbms.map((pbm) => (
                    <MenuItem value={pbm} key={pbm.code}>
                      {pbm.name}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Value */}
                <NumericFormat
                  customInput={TextField}
                  label="Value"
                  sx={{ flex: '0 0 25%' }}
                  thousandSeparator
                  value={row.value}
                  onValueChange={(value) => {
                    handleRowValueChange(index, value.value);
                  }}
                  onPaste={(event) => handlePasteValues(event, index)}
                />
                {/* Calculated Rate */}
                <NumericFormat
                  customInput={TextField}
                  label="Rate"
                  value={row.rate == null ? 0 : row.rate * 100}
                  disabled
                  decimalScale={2}
                  fixedDecimalScale
                  thousandSeparator
                  suffix="%"
                  sx={{ flex: '0 0 15%' }}
                />
              </Box>
            ))}
          </Box>
        </>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} data-test-id="cancel-button">Cancel</Button>
        <Button onClick={submit} data-test-id="submit-button">Submit</Button>
      </DialogActions>
    </Dialog>
  );
}